import { useMemo, useState } from "react";
import {
  useGetEnterpriseInquiriesQuery,
  useUpdateEnterpriseInquiryMutation,
} from "../../../redux/api";
import type { EnterpriseInquiry } from "../../../redux/api";
import { IconSearch } from "../../../components/icons";

const COLUMN_COUNT = 6;

const STATUS_TONE: Record<string, string> = {
  new: "badge--info",
  contacted: "badge--warning",
  closed: "badge--success",
};

const statusTone = (status?: string | null) =>
  STATUS_TONE[(status ?? "").toLowerCase()] ?? "badge--info";

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : "—";

// Platform Owner → Enterprise. Sales inquiries submitted from the landing page.
const Enterprise = () => {
  const {
    data: inquiries = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetEnterpriseInquiriesQuery();

  const [updateInquiry] = useUpdateEnterpriseInquiryMutation();

  const [search, setSearch] = useState("");

  const setStatus = async (inquiry: EnterpriseInquiry, status: string) => {
    try {
      await updateInquiry({ id: inquiry.id, status }).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to update inquiry.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return inquiries;
    return inquiries.filter((i: EnterpriseInquiry) =>
      [i.name, i.email, i.company, i.status].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [inquiries, search]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load inquiries"
    : null;

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Enterprise inquiries</h2>
            <p className="panel__sub">
              Showing {visible.length} of {inquiries.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search inquiries…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Message</th>
                <th>Status</th>
                <th>Set status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading inquiries…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {inquiries.length === 0
                      ? "No enterprise inquiries yet."
                      : "No inquiries match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((inquiry: EnterpriseInquiry) => (
                  <tr key={inquiry.id}>
                    <td className="cell-title">
                      {inquiry.name}
                      <div className="cell-date">{formatDate(inquiry.createdAt)}</div>
                    </td>
                    <td className="cell-date">{inquiry.email}</td>
                    <td>{inquiry.company ?? "—"}</td>
                    <td>{inquiry.message ?? "—"}</td>
                    <td>
                      <span className={`badge ${statusTone(inquiry.status)}`}>
                        <span className="badge__dot" />
                        {inquiry.status}
                      </span>
                    </td>
                    <td>
                      <select
                        className="select select--sm"
                        value={inquiry.status}
                        onChange={(e) => setStatus(inquiry, e.target.value)}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default Enterprise;
